import { useState } from "react";
import { Check, Clipboard, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export function CopyableSummary({ summaryText, assessmentId }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      toast.success("Snapshot copied to clipboard", {
        description: "You can paste this into any governance document or email draft."
      });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Unable to copy automatically. Please copy the text manually.");
    }
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([summaryText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${assessmentId || "readiness-snapshot"}.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded", { description: "Text file saved locally." });
    } catch {
      toast.error("Unable to start the download.");
    }
  };

  return (
    <section className="paper-card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-border bg-card/80 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="eyebrow">§ 10 · Copyable output</p>
          <h3 className="font-display text-lg font-medium text-foreground">
            Plain-text snapshot
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleDownload}
            className="rounded-sm border-border bg-card text-[13px] font-medium hover:bg-secondary"
          >
            <Download className="h-4 w-4" />
            Download .txt
          </Button>
          <Button
            type="button"
            onClick={handleCopy}
            className="rounded-sm px-5 text-[13px] font-medium tracking-wide shadow-paper"
          >
            {copied ? <Check className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
            {copied ? "Copied" : "Copy snapshot"}
          </Button>
        </div>
      </div>
      <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-words bg-card px-6 py-5 font-mono text-[12.5px] leading-relaxed text-foreground/90">
        {summaryText}
      </pre>
    </section>
  );
}
